import { CreditCard } from 'lucide-react';
import PaymentMethods from '../PaymentMethods';
import { useCurrencyState } from '../../stores/subscriptionSelectors';
import { PaymentMethod } from '../../stores/subscriptionStore';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';

// Define which payment methods are supported for each currency
const SUPPORTED_PAYMENT_METHODS: Record<string, PaymentMethod[]> = {
  thb: ['card', 'promptpay'],
  usd: ['card', 'crypto']
};

interface PaymentMethodsSectionProps {
  selectedMethod: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  // Optional props for Purchase Journey tracking
  trackingData?: {
    subscriptionLevel: 'standard' | 'premium';
    subscriptionType: 'monthly' | 'yearly' | 'trial';
    serviceType: 'flower' | 'fruit' | 'cat_grass';
    currency: string;
    price: number;
  };
}

export const PaymentMethodsSection = ({
  selectedMethod,
  onSelect,
  trackingData
}: PaymentMethodsSectionProps) => {
  // Get current currency
  const { currency } = useCurrencyState();
  const { t } = useTranslation();
  
  // Track previous currency and payment method to detect changes
  const prevCurrencyRef = useRef(currency);
  const prevSelectedMethodRef = useRef(selectedMethod);
  const hasShownToastRef = useRef(false);
  
  // Get supported payment methods for current currency
  const supportedMethods = SUPPORTED_PAYMENT_METHODS[currency] || ['card'];
  
  // Effect to handle payment method switching when currency changes
  useEffect(() => {
    const prevCurrency = prevCurrencyRef.current;
    const prevSelectedMethod = prevSelectedMethodRef.current;
    
    // Check if currency has changed and the current payment method is not supported
    if (prevCurrency !== currency && 
        selectedMethod && 
        !supportedMethods.includes(selectedMethod) &&
        !hasShownToastRef.current) {
      
      // Get the payment method name for display
      const getPaymentMethodName = (method: PaymentMethod) => {
        switch (method) {
          case 'card': return t('subscription.paymentMethods.card', 'Credit/Debit Card');
          case 'promptpay': return t('subscription.paymentMethods.promptpay', 'PromptPay');
          case 'crypto': return t('subscription.paymentMethods.crypto', 'Cryptocurrency');
          case 'qr': return t('subscription.paymentMethods.qr', 'QR Code');
          default: return method;
        }
      };
      
      const newPaymentMethod = supportedMethods[0];
      const oldMethodName = getPaymentMethodName(selectedMethod);
      const newMethodName = getPaymentMethodName(newPaymentMethod);
      
      // Show toast notification
      toast(
        t('subscription.paymentMethods.autoSwitched', 
          `Payment method switched from ${oldMethodName} to ${newMethodName} due to currency change`,
          { oldMethod: oldMethodName, newMethod: newMethodName }
        ),
        { 
          duration: 4000,
          icon: 'ℹ️'
        }
      );
      
      // Switch to the first supported payment method
      onSelect(newPaymentMethod);
      hasShownToastRef.current = true;
      
      // Reset the toast flag after a delay to allow future switches
      setTimeout(() => {
        hasShownToastRef.current = false;
      }, 5000);
    }
    
    // Update refs for next comparison
    prevCurrencyRef.current = currency;
    prevSelectedMethodRef.current = selectedMethod;
  }, [currency, selectedMethod, supportedMethods, onSelect, t]);
  
  // Legacy fallback - if currently selected method is not supported by the currency, select the first supported one
  // This handles cases where the effect above doesn't catch the change
  if (selectedMethod && !supportedMethods.includes(selectedMethod) && !hasShownToastRef.current) {
    // We need to wait for the next render cycle to update this
    setTimeout(() => {
      onSelect(supportedMethods[0]);
    }, 0);
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <CreditCard size={20} className="text-gray-500 mr-2" />
          <span className="text-base font-medium text-gray-900">{t('subscription.paymentMethod')}</span>
        </div>
      </div>
      <PaymentMethods
        selectedMethod={selectedMethod}
        onSelect={(method) => onSelect(method as PaymentMethod)}
        disabledMethods={Object.values(SUPPORTED_PAYMENT_METHODS)
          .flat()
          .filter(method => !supportedMethods.includes(method))}
        trackingData={trackingData}
      />
    </div>
  );
};

export default PaymentMethodsSection; 